<template lang="pug">
#family-confirm-result-tab
  .register-body.mt-4
    FormLabel(key-name="受付状態" :value="_acceptingOtherUserReply")

    .replies
      .reply(v-for="reply in replies")
        .datetime.d-flex.flex-row
          span.item-name 送信日時：
          span.item-value {{ convertSendDateTime(reply.datetime) }}
        .target.d-flex.flex-row
          span.item-name 宛先：
          span.item-value {{ reply.targetName }}
        .buttons.d-flex.flex-row
          // 確認者一覧
          .loading.mr-2(v-if="reply.readedFamilies == null")
            b-spinner.spinner
            span 確認者読込中...
          button.btn.readed-families-btn.mr-2(v-else @click="onReadedFamilies(reply.readedFamilies)") 確認者一覧（{{ reply.readedFamilies.length }}人）
          // 未確認者一覧
          .loading(v-if="reply.unreadFamilies == null")
            b-spinner.spinner
            span 未確認者読込中...
          button.btn.unread-families-btn(v-else @click="onUnreadFamilies(reply.unreadFamilies)") 未確認者一覧（{{ reply.unreadFamilies.length }}人）

    .error-message(v-show="errorMessage")
      span {{ errorMessage }}

  .box-footer.register-footer
    .buttons.m-0
      button.btn.change-status-btn(@click="onChangeStatus(safetyConfirm)" type="button") 受付状態の変更
      button.btn.back-btn(@click="onBack" type="button") 戻る

  SafetyConfirmFamiliesModal(v-model="showSafetyConfirmFamiliesModal" :families="showTargetFamilies")

  RelativeLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";
import Setting from "../../../../public/setting.json";

import SafetyConfirmsService, { SafetyConfirmsGetModel } from "@/commons/services/SafetyConfirms";
import SendMessageReadFamiliesService, { SendMessageReadFamilyModel } from "@/commons/services/SendMessageReadFamilies";
import SendSafetyConfirmMessagesService, { SendFamilySafetyConfirmLineMessagesGetModel, SendSafetyConfirmReceiverDetail } from "@/commons/services/SendSafetyConfirmMessages";

import RelativeLoading from "@/pc/basics/RelativeLoading.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import SafetyConfirmFamiliesModal from "@/pc/components/modals/SafetyConfirmFamiliesModal.vue";
import SystemError from "@/commons/models/SystemError";

/**
 * ### 安否確認(家族)送信タブコンポーネント
 */
@Component({
  components: {
    RelativeLoading,
    FormLabel,
    SafetyConfirmFamiliesModal
  }
})
export default class FamilyConfirmResultTab extends Vue {
  /** 安否確認サービス */
  @Inject("SafetyConfirmsService") safetyConfirmsService!: SafetyConfirmsService;
  /** 送信メッセージにひもづく家族サービス */
  @Inject("SendMessageReadFamiliesService") sendMessageReadFamiliesService!: SendMessageReadFamiliesService;
  /** 安否確認(施設関係者)メッセージ送信サービス */
  @Inject("SendSafetyConfirmMessagesService") sendSafetyConfirmMessagesService!: SendSafetyConfirmMessagesService;

  /** ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "読込み中...";
  /** 確認者家族モーダル表示可否 */
  showSafetyConfirmFamiliesModal: boolean = false;
  /** 確認者家族モーダル表示対象の家族 */
  showTargetFamilies: Array<SendMessageReadFamilyModel> = [];

  /** 災害ID */
  safetyConfirmsId: number = 0;
  /** 安否確認受付状態 */
  acceptingFamilyReply: boolean = false;
  /** 安否確認 */
  safetyConfirm: SafetyConfirmsGetModel | null = null;
  /** 家族からの返信 */
  replies: Array<FamilyReplySafetyConfirm> = [];

  /** エラーメッセージ */
  @Prop({ required: true, type: String })
  errorMessage!: string;

  get _acceptingOtherUserReply(): string {
    if (this.acceptingFamilyReply == true) {
      return "受付中";
    } else {
      return "受付停止";
    }
  }

  /**
   * ### 画面情報を再作成する
   */
  public async refresh(): Promise<void> {
    this.createView();

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const safetyConfirmsId: number = Number(this.$route.params.id);
    this.safetyConfirmsId = safetyConfirmsId;

    const safetyConfirm: SafetyConfirmsGetModel = await this.getSafetyConfirm(safetyConfirmsId);
    this.safetyConfirm = safetyConfirm;
    this.acceptingFamilyReply = safetyConfirm.acceptingFamilyReply;

    const replies: Array<FamilyReplySafetyConfirm> = [];
    if (safetyConfirm.messagesSortKey != null) {
      for (const messagesSortKey of safetyConfirm.messagesSortKey) {
        const splitedSortKey: string[] = messagesSortKey.split("_");
        replies.push({
          sortKey: messagesSortKey,
          datetime: Number(splitedSortKey[2]),
          targetName: "",
          readedFamilies: null,
          unreadFamilies: null
        });
      }
    }
    this.replies = replies;

    this.isBusy = false;

    // 画面表示後、グループ名、家族を取得する
    // DBのスループット上限問題、chromeのリクエスト上限を回避するために50件ずつ取得し、取得する毎に1秒インターバルを挟む
    let promises: Array<Promise<void>> = [];
    for (let i = 0; i < this.replies.length; i++) {
      promises.push(this.setGroupName(this.replies[i]));
      promises.push(this.setFamilies(this.replies[i]));

      if (Setting.requestLimit < promises.length) {
        const results: Array<any> = await Promise.all(promises);
        // 非同期処理中に例外が発生していればresultの中にSystemerrorが格納されているのでチェックする
        for (const result of results) {
          if (result instanceof SystemError) {
            errorHandle(result, this);
          }
        }
        promises = [];
        await this.$timerUtil.setTimeout(1000);
      }
    }
    let results: Array<any> = await Promise.all(promises);
    for (const result of results) {
      if (result instanceof SystemError) {
        errorHandle(result, this);
      }
    }

    return;
  }

  /**
   * ### 安否確認を取得する
   */
  public async getSafetyConfirm(safetyConfirmsId: number): Promise<SafetyConfirmsGetModel> {
    const response: SafetyConfirmsGetModel = await this.safetyConfirmsService.get(userStore, safetyConfirmsId);
    return response;
  }

  public setGroupName(reply: FamilyReplySafetyConfirm): Promise<void> {
    return new Promise(async (resolve: Function, reject: Function) => {
      try {
        const safetyConfirmMessage: SendFamilySafetyConfirmLineMessagesGetModel = await this.getSafetyConfirmMessage(reply.sortKey);
        const details: Array<SendSafetyConfirmReceiverDetail> = safetyConfirmMessage.receiverDetails as Array<SendSafetyConfirmReceiverDetail>;
        const receivers: string[] = [];
        for (const receiver of details) {
          receivers.push(receiver.groupName);
        }
        reply.targetName = [...new Set(receivers)].join("、");
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * ### 安否確認(施設関係者)メッセージを取得する
   */
  public async getSafetyConfirmMessage(parentMessagesSortKey: string): Promise<SendFamilySafetyConfirmLineMessagesGetModel> {
    const response: SendFamilySafetyConfirmLineMessagesGetModel = await this.sendSafetyConfirmMessagesService.getFamilyMessage(userStore, parentMessagesSortKey);
    return response;
  }

  /**
   * ### 家族返信の情報を設定する
   */
  public setFamilies(reply: FamilyReplySafetyConfirm): Promise<void> {
    return new Promise(async (resolve: Function, reject: Function) => {
      try {
        const families: Array<SendMessageReadFamilyModel> = await this.getSendMessageReadFamilies(reply.datetime);

        let readedFamilies: Array<SendMessageReadFamilyModel> = [];
        let unreadFamilies: Array<SendMessageReadFamilyModel> = [];
        for (const family of families) {
          if (family.readDatetime != undefined || family.readDatetime != null) {
            readedFamilies.push(family);
          } else {
            unreadFamilies.push(family);
          }
        }

        reply.readedFamilies = readedFamilies;
        reply.unreadFamilies = unreadFamilies;
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * ### メッセージ送信したメッセージにひもづく家族を取得する
   */
  public async getSendMessageReadFamilies(datetime: number): Promise<Array<SendMessageReadFamilyModel>> {
    const families: Array<SendMessageReadFamilyModel> = await this.sendMessageReadFamiliesService.get(userStore, datetime);
    return families;
  }

  /**
   * ### 送信日時を表示用文字列に変換する
   */
  public convertSendDateTime(datetime: number): string {
    const displayDatetime: string = this.$dateUtil.convertNumberToString(datetime, "yyyy年mm月dd日 hh:MM");
    return displayDatetime;
  }

  /**
   * ### 確認者家族をモーダル表示する
   */
  public onReadedFamilies(readedFamilies: Array<SendMessageReadFamilyModel>): void {
    this.showTargetFamilies = readedFamilies;
    this.showSafetyConfirmFamiliesModal = true;

    return;
  }

  /**
   * ### 未確認者家族をモーダル表示する
   */
  public onUnreadFamilies(unreadFamilies: Array<SendMessageReadFamilyModel>): void {
    this.showTargetFamilies = unreadFamilies;
    this.showSafetyConfirmFamiliesModal = true;

    return;
  }

  /**
   * ### 受付状態変更ボタン押下イベント
   */
  public onChangeStatus(safetyConfirm: SafetyConfirmsGetModel): void {
    this.$emit("onChangeStatus", safetyConfirm);
    return;
  }

  /**
   * ### 戻るボタン押下イベント
   */
  public onBack(): void {
    this.$emit("onBack");
    return;
  }
}

interface FamilyReplySafetyConfirm {
  /** messagesテーブルのソートキー(0_0_{datetime}) */
  sortKey: string;
  /** datetime */
  datetime: number;
  /** 宛先名 */
  targetName: string;
  /** 既読家族 */
  readedFamilies: Array<SendMessageReadFamilyModel> | null;
  /** 未読家族 */
  unreadFamilies: Array<SendMessageReadFamilyModel> | null;
}
</script>

<style lang="scss" scoped>
#family-confirm-result-tab {
  position: relative;

  .replies {
    margin-left: 20px;

    .reply {
      margin-bottom: 20px;

      .datetime,
      .target {
        margin-bottom: 4px;
        .item-name {
          font-weight: bold;
        }
        .item-name,
        .item-value {
          font-size: 18px;
        }
      }
      .readed-families-btn,
      .unread-families-btn {
        min-width: 170px;
      }
    }
  }

  .change-status-btn,
  .back-btn {
    width: 150px;
  }

  .box-footer {
    padding: 10px 0 0 0 !important;
  }
}
</style>
